import React from 'react'
import { Link } from 'react-router-dom'
import { ArrowUpRight } from 'lucide-react'
import { ChatToolCard } from '@/components'
import type { ToolCardStatus, ToolConfigEntry } from '@/components'
import { MESSAGE_STATUS } from '../../contexts/SSEEvents'
import type { MessageStatus } from '../api'
import BacktestResultCard from './BacktestResultCard'

// Loose config shape: the runtime payload from the AI server is a nested object
// whose exact schema we don't statically own here. We only read the fields the
// new tool card surfaces (symbol / timeframe / period).
interface BacktestConfigShape {
  backtest_config?: {
    symbol?: string
    timeframe?: string
    start_date?: string
    end_date?: string
  }
}

interface BacktestToolData {
  toolName: string
  toolInput: {
    instructions?: string
  }
  startedAt: string
  completedAt?: string
  status: 'running' | 'completed'
  toolStatus?: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  config?: Record<string, any>
  btid?: string
  version?: number
  codeVersion?: number
  error?: string
}

interface BotMessageToolBacktestProps {
  toolData: BacktestToolData
  status?: MessageStatus
  /** Chat msgId — anchors the results badge (and the first-run coach) to this message. */
  msgId?: string
  onRunOptimizer?: () => void
  onViewReport?: () => void
  /** Chat is busy — the badge's actions can't be used right now. */
  actionsDisabled?: boolean
}

// Live stage labels (only shown while running). Driven by real SSE toolStatus
// updates, not synthetic cycling.
const RUNNING_STAGES: Record<string, string> = {
  BACKTEST_STARTED: 'Starting backtest',
  BACKTEST_PREPARING: 'Preparing configuration',
  BACKTEST_CONFIGURATION: 'Generating backtest parameters',
  BACKTEST_QUEUED: 'Backtest queued',
  BACKTEST_RUNNING: 'Running backtest',
}

export default function BotMessageToolBacktest({
  toolData,
  status,
  msgId,
  onRunOptimizer,
  onViewReport,
  actionsDisabled,
}: BotMessageToolBacktestProps) {
  const cardStatus = resolveStatus(status, toolData.status, toolData.toolStatus)
  const elapsedTime = useElapsedTime(toolData.startedAt, toolData.completedAt, cardStatus === 'running')

  const stage = cardStatus === 'running' ? RUNNING_STAGES[toolData.toolStatus ?? ''] : undefined
  const config = buildConfig(toolData, cardStatus)

  // A live run completing (running → done within this mounted lifetime)
  // reveals the results badge — it grows an EXISTING message, so BotChat's
  // append-based autoscroll never fires. History mounts render already-done
  // and must not yank the feed (the initial load owns positioning). The ref
  // is only touched inside the effect (react-hooks/refs).
  const prevStatusRef = React.useRef(cardStatus)
  React.useEffect(() => {
    const prev = prevStatusRef.current
    prevStatusRef.current = cardStatus
    if (prev !== 'running' || cardStatus !== 'done' || !msgId) return
    document
      .querySelector(`[data-coach="backtest-card"][data-msg-id="${msgId}"]`)
      ?.scrollIntoView?.({ block: 'nearest', behavior: 'smooth' })
  }, [cardStatus, msgId])

  return (
    <div className="space-y-2">
      <ChatToolCard
        message={{
          id: 0,
          type: 'tool',
          label: labelFor('Running Backtest', cardStatus),
          status: cardStatus,
          stage,
          duration: cardStatus === 'done' ? formatTime(elapsedTime) : undefined,
          config,
        }}
      />
      {cardStatus === 'done' && toolData.btid && msgId && (
        <BacktestResultCard
          btid={toolData.btid}
          msgId={msgId}
          onRunOptimizer={onRunOptimizer}
          onViewReport={onViewReport}
          actionsDisabled={actionsDisabled}
        />
      )}
    </div>
  )
}

function labelFor(base: string, status: ToolCardStatus) {
  if (status === 'canceled') return `${base} (Canceled)`
  if (status === 'error') return `${base} (Failed)`
  return base
}

function resolveStatus(
  status: MessageStatus | undefined,
  toolStatus: BacktestToolData['status'],
  serverStatus: string | undefined,
): ToolCardStatus {
  if (serverStatus === 'BACKTEST_CANCELED' || status === MESSAGE_STATUS.CANCELED) return 'canceled'
  if (
    status === MESSAGE_STATUS.FAILED ||
    serverStatus === 'BACKTEST_ERROR' ||
    serverStatus === 'BACKTEST_TIMEOUT'
  ) {
    return 'error'
  }
  if (
    status === MESSAGE_STATUS.PROCESSED ||
    toolStatus === 'completed' ||
    serverStatus === 'BACKTEST_COMPLETED'
  ) {
    return 'done'
  }
  return 'running'
}

function buildConfig(
  toolData: BacktestToolData,
  cardStatus: ToolCardStatus,
): ToolConfigEntry[] | undefined {
  const bc = (toolData.config as BacktestConfigShape | undefined)?.backtest_config
  const entries: ToolConfigEntry[] = []

  if (bc) {
    entries.push({ label: 'Symbol', value: bc.symbol ?? '' })
    entries.push({ label: 'Timeframe', value: bc.timeframe ?? '' })
    entries.push({ label: 'Period', value: `${bc.start_date ?? ''} – ${bc.end_date ?? ''}` })
  }

  if (
    cardStatus === 'done' &&
    (toolData.btid || toolData.version != null || toolData.codeVersion != null)
  ) {
    entries.push({
      label: 'Version',
      value: (
        <VersionValue
          version={formatVersion(toolData.codeVersion, toolData.version)}
          to={toolData.btid ? `/backtest/${toolData.btid}` : undefined}
        />
      ),
    })
  }

  return entries.length > 0 ? entries : undefined
}

function useElapsedTime(startedAt: string | undefined, completedAt: string | undefined, isRunning: boolean) {
  const [tickElapsed, setTickElapsed] = React.useState(0)

  React.useEffect(() => {
    if (!startedAt || !isRunning) return
    const start = new Date(startedAt).getTime()
    const interval = setInterval(() => {
      setTickElapsed((Date.now() - start) / 1000)
    }, 100)
    return () => clearInterval(interval)
  }, [startedAt, isRunning])

  if (!isRunning && startedAt && completedAt) {
    const start = new Date(startedAt).getTime()
    const end = new Date(completedAt).getTime()
    return (end - start) / 1000
  }
  return tickElapsed
}

function formatTime(seconds: number) {
  return `${seconds.toFixed(1)}s`
}

function formatVersion(codeVersion?: number, version?: number) {
  if (codeVersion != null && version != null) return `v${codeVersion}.${version}`
  if (version != null) return `v${version}`
  if (codeVersion != null) return `v${codeVersion}`
  return null
}

function VersionValue({ version, to }: { version: string | null; to?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {version && <span className="font-mono tabular-nums">{version}</span>}
      {to && (
        <Link
          to={to}
          aria-label="Open backtest details"
          className="inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
        >
          <ArrowUpRight size={12} strokeWidth={1.75} />
        </Link>
      )}
    </span>
  )
}
